import {
	BASE_PRECISION_EXP,
	BN,
	BigNum,
	MarketType,
	OrderAction,
	OrderActionExplanation,
	PositionDirection,
	PRICE_PRECISION_EXP,
	PublicKey,
	QUOTE_PRECISION_EXP,
	SpotMarketConfig,
	WrappedEvent,
} from '@velocity-exchange/sdk';
import { ENUM_UTILS, UIMarket } from '@velocity-exchange/common';

export type RawTradeOutput = {
	action: string;
	actionExplanation: string;
	baseAssetAmountFilled: number;
	filler: string;
	maker: string;
	makerFee: number;
	makerOrderBaseAssetAmount: number;
	makerOrderCumulativeBaseAssetAmountFilled: number;
	makerOrderCumulativeQuoteAssetAmountFilled: number;
	makerOrderDirection: 'long' | 'short';
	makerOrderId: number;
	marketIndex: number;
	marketType: 'perp' | 'spot';
	oraclePrice: number;
	quoteAssetAmountFilled: number;
	quoteAssetAmountSurplus: number;
	referrer: string;
	referrerReward: number;
	slot: number;
	taker: string;
	takerFee: number;
	takerOrderBaseAssetAmount: number;
	takerOrderCumulativeBaseAssetAmountFilled: number;
	takerOrderCumulativeQuoteAssetAmountFilled: number;
	takerOrderDirection: 'perp' | 'spot';
	takerOrderId: number;
	ts: number;
	txSig: string;
	takerPnl: number;
	makerPnl: number;
	makerRebate: number;
	spotFulfillmentMethodFee: number;
	fillRecordId: number;
	refereeDiscount: number;
	txSigIndex: number;
	takerExistingQuoteEntryAmount: number;
	takerExistingBaseAssetAmount: number;
	makerExistingQuoteEntryAmount: number;
	makerExistingBaseAssetAmount: number;
};

export const deserializeTradeResponse = (
	serializedTrade: RawTradeOutput
): WrappedEvent<'OrderActionRecord'> => {
	const market = new UIMarket(
		serializedTrade.marketIndex,
		ENUM_UTILS.toObj(serializedTrade.marketType) as MarketType
	);
	const basePrecision = market.isPerp
		? BASE_PRECISION_EXP
		: (market.market as SpotMarketConfig).precisionExp;

	return {
		eventType: 'OrderActionRecord',
		txSig: serializedTrade.txSig,
		slot: serializedTrade.slot,
		ts: new BN(serializedTrade.ts),
		action: ENUM_UTILS.toObj(serializedTrade.action) as OrderAction,
		actionExplanation: ENUM_UTILS.toObj(
			serializedTrade.actionExplanation
		) as OrderActionExplanation,
		marketIndex: serializedTrade.marketIndex,
		marketType: ENUM_UTILS.toObj(serializedTrade.marketType) as MarketType,
		filler: serializedTrade.filler
			? new PublicKey(serializedTrade.filler)
			: null,
		referrerReward: serializedTrade.referrerReward,
		fillRecordId: serializedTrade.fillRecordId
			? new BN(serializedTrade.fillRecordId)
			: null,
		baseAssetAmountFilled: BigNum.fromPrint(
			serializedTrade.baseAssetAmountFilled.toString(),
			basePrecision
		).val,
		quoteAssetAmountFilled: BigNum.fromPrint(
			serializedTrade.quoteAssetAmountFilled.toString(),
			QUOTE_PRECISION_EXP
		).val,
		takerFee: BigNum.fromPrint(
			serializedTrade.takerFee?.toString() ?? '0',
			QUOTE_PRECISION_EXP
		).val,
		quoteAssetAmountSurplus: BigNum.fromPrint(
			serializedTrade.quoteAssetAmountSurplus?.toString() ?? '0',
			QUOTE_PRECISION_EXP
		).val,
		takerOrderBaseAssetAmount: BigNum.fromPrint(
			serializedTrade.takerOrderBaseAssetAmount?.toString() ?? '0',
			basePrecision
		).val,
		takerOrderCumulativeBaseAssetAmountFilled: BigNum.fromPrint(
			serializedTrade.takerOrderCumulativeBaseAssetAmountFilled?.toString() ??
				'0',
			basePrecision
		).val,
		takerOrderCumulativeQuoteAssetAmountFilled: BigNum.fromPrint(
			serializedTrade.takerOrderCumulativeQuoteAssetAmountFilled?.toString() ??
				'0',
			QUOTE_PRECISION_EXP
		).val,
		makerOrderBaseAssetAmount: BigNum.fromPrint(
			serializedTrade.makerOrderBaseAssetAmount?.toString() ?? '0',
			basePrecision
		).val,
		makerOrderCumulativeBaseAssetAmountFilled: BigNum.fromPrint(
			serializedTrade.makerOrderCumulativeBaseAssetAmountFilled?.toString() ??
				'0',
			basePrecision
		).val,
		makerOrderCumulativeQuoteAssetAmountFilled: BigNum.fromPrint(
			serializedTrade.makerOrderCumulativeQuoteAssetAmountFilled?.toString() ??
				'0',
			QUOTE_PRECISION_EXP
		).val,
		oraclePrice: BigNum.fromPrint(
			serializedTrade.oraclePrice?.toString() ?? '0',
			PRICE_PRECISION_EXP
		).val,
		makerFee: BigNum.fromPrint(
			serializedTrade.makerFee?.toString() ?? '0',
			QUOTE_PRECISION_EXP
		).val,
		fillerReward: BigNum.fromPrint(
			serializedTrade.makerFee?.toString() ?? '0',
			QUOTE_PRECISION_EXP
		).val,
		txSigIndex: serializedTrade.txSigIndex,
		taker: serializedTrade.taker ? new PublicKey(serializedTrade.taker) : null,
		takerOrderId: serializedTrade.takerOrderId,
		takerOrderDirection: ENUM_UTILS.toObj(
			serializedTrade.takerOrderDirection
		) as PositionDirection,
		maker: serializedTrade.maker ? new PublicKey(serializedTrade.maker) : null,
		makerOrderId: serializedTrade.makerOrderId,
		makerOrderDirection: ENUM_UTILS.toObj(
			serializedTrade.makerOrderDirection
		) as PositionDirection,
		spotFulfillmentMethodFee: serializedTrade.spotFulfillmentMethodFee
			? new BN(serializedTrade.spotFulfillmentMethodFee)
			: null,
		// TODO: pull in bit flags / builder fields once available on dlob server
		bitFlags: 0,
		triggerPrice: null,
		builderIdx: null,
		builderFee: null,
		takerExistingQuoteEntryAmount: BigNum.fromPrint(
			serializedTrade.takerExistingQuoteEntryAmount?.toString() ?? '0',
			QUOTE_PRECISION_EXP
		).val,
		takerExistingBaseAssetAmount: BigNum.fromPrint(
			serializedTrade.takerExistingBaseAssetAmount?.toString() ?? '0',
			basePrecision
		).val,
		makerExistingQuoteEntryAmount: BigNum.fromPrint(
			serializedTrade.makerExistingQuoteEntryAmount?.toString() ?? '0',
			QUOTE_PRECISION_EXP
		).val,
		makerExistingBaseAssetAmount: BigNum.fromPrint(
			serializedTrade.makerExistingBaseAssetAmount?.toString() ?? '0',
			basePrecision
		).val,
	};
};
